import { useState } from 'react';
import { tmdbService } from '../services/tmdbService';

const TMDBSearch = ({ onSelectMovie, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const data = await tmdbService.searchMovies(query);
      setResults(data.results.slice(0, 6)); // Show top 6 results
    } catch (err) {
      setError('Failed to search movies. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickSelect = (movie) => {
    // Quick select with basic info
    const basicData = {
      title: movie.title,
      description: movie.overview,
      genre: '',
      year: movie.release_date ? parseInt(movie.release_date.split('-')[0]) : '',
      poster: tmdbService.getImageUrl(movie.poster_path, 'w500') || '',
      trailer: '',
      rating: Math.min(Math.max(movie.vote_average / 2, 0), 5).toFixed(1)
    };
    onSelectMovie(basicData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-accent-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 3v2h-2V3H8v2H6V3H4v18h2v-2h2v2h8v-2h2v2h2V3h-2zM8 17H6v-2h2v2zm0-4H6v-2h2v2zm0-4H6V7h2v2zm10 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z"/>
              </svg>
              <div>
                <h2 className="text-xl font-bold">Search Movies</h2>
                <p className="text-sm text-primary-100">Find movies from our database</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="mt-4 flex gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for a movie..."
              className="flex-1 px-4 py-2 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50"
            />
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="px-6 py-2 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>Search</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Results */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {!loading && results.length === 0 && query && (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.467-.881-6.082-2.291M15 6.306a7.962 7.962 0 00-6.083-2.291C8.39 4.04 8 4.04 8 4.04v14.917c0 .593.192 1.173.54 1.648.347.475.842.826 1.418.997.576.171 1.202.15 1.76-.058.559-.208 1.034-.58 1.362-1.063.328-.483.495-1.054.478-1.631-.017-.577-.214-1.137-.566-1.6-.352-.463-.84-.794-1.392-.936-.552-.142-1.134-.086-1.658.161-.524.247-.952.634-1.23 1.11-.278.476-.393 1.017-.33 1.548M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p>No movies found. Try a different search.</p>
            </div>
          )}

          {results.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.map((movie) => (
                <div 
                  key={movie.id}
                  className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-200 hover:border-primary-300"
                  onClick={() => handleQuickSelect(movie)}
                >
                  <div className="relative aspect-[2/3]">
                    {movie.poster_path ? (
                      <img
                        src={tmdbService.getImageUrl(movie.poster_path, 'w342')}
                        alt={movie.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <button className="w-full bg-primary-600 text-white py-2 rounded-lg font-medium text-sm hover:bg-primary-700 transition-colors">
                          Select Movie
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-gray-900 line-clamp-1 text-sm">{movie.title}</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {movie.release_date ? new Date(movie.release_date).getFullYear() : 'N/A'} • 
                      Rating: {movie.vote_average.toFixed(1)}/10
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!query && !loading && (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-lg font-medium">Start searching for movies</p>
              <p className="text-sm mt-1">Type a movie title above to search TMDB</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Powered by TMDB • Data provided by The Movie Database
          </p>
        </div>
      </div>
    </div>
  );
};

export default TMDBSearch;
